import {l} from "types/typescriptdefinisjoner_lederplan";
import {BasisRessurs} from "felles/infrastruktur/BasisRessurs";
import {RessursFeilhåndtering} from "../RessursFeilhåndtering";
import {gettextCatalog} from "angular-gettext";
import _ from "lodash";
import {regNavn} from "../tredjepartsTjenestenavn";
import {Debugger} from "debug";

const debug:Debugger = require('debug')('GruppeMedlemRessurs');

export class GruppeMedlemRessurs extends BasisRessurs<l.GruppeMedlemModell> {

    static regNavn = "GruppeMedlemRessurs";

    static forInjisering() {
        return [
            regNavn.ng.$resource,
            RessursFeilhåndtering.regNavn,
            regNavn.gettext.gettextCatalog,
            GruppeMedlemRessurs
        ]
    }

    constructor($resource: ng.resource.IResourceService, ressursFeilhåndtering: RessursFeilhåndtering, gettextCatalog: gettextCatalog) {
        super("medlem",
            $resource,
            ressursFeilhåndtering,
            gettextCatalog,
            {
                assosiasjoner: {method: 'GET', url: "/api/medlem/:id/assosiasjoner"},
                oppdaterTilgang: {method: 'POST', url: "/api/medlem/:id/oppdaterTilgang"},
                åpneTilgang: {method: 'POST', url: "/api/medlem/:id/aapneTilgang"},
                fjernTilgang: {method: 'POST', url: "/api/medlem/:id/fjernTilgang"},
                anonymiser: {method: 'POST', url: "/api/medlem/:id/anonymiser"}
            });
    }

    assosiasjoner(medlemId: string): l.MedlemAssosiasjonerModell {
        debug("assosiasjoner", medlemId);
        return this.ressurs['assosiasjoner'](
            {id: medlemId},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke finne medlemmets koblinger"))
        );
    }

    oppdater(spesifikasjon: l.OppdaterMedlemSpesifikasjon, vedOk: (entitet: l.GruppeMedlemModell) => void = _.noop): l.GruppeMedlemModell {
        return super.oppdater(spesifikasjon, vedOk);
    }

    oppdaterTilgang(spesifikasjon: l.OppdaterMedlemTilgangSpesifikasjon): l.GruppeMedlemModell {
        debug("oppdaterTilgang", spesifikasjon);
        return this.ressurs['oppdaterTilgang'](
            spesifikasjon,
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke oppdatere tilgang for medlem"))
        );
    }

    åpneTilgang(medlemId:string, spesifikasjon: l.MedlemÅpneTilgangSpesifikasjon): l.GruppeMedlemModell {
        debug("åpneTilgang", spesifikasjon);
        return this.ressurs['åpneTilgang'](
            _.defaults({id:medlemId}, spesifikasjon),
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke oppdatere tilgang for medlem"))
        );
    }

    fjernTilgang(medlemId:string, spesifikasjon: l.MedlemStengTilgangSpesifikasjon): l.GruppeMedlemModell {
        debug("fjernTilgang", spesifikasjon);
        return this.ressurs['fjernTilgang'](
            _.defaults({id:medlemId}, spesifikasjon),
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke oppdatere tilgang for medlem"))
        );
    }

    anonymiser(medlemId:string, årsak: string): l.GruppeMedlemModell {
        debug("anonymiser", årsak);
        return this.ressurs['anonymiser'](
            {id:medlemId, aarsak: årsak},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke anonymisere medlem"))
        );
    }

    tilgangsrollerMedlemValg(): TilgangsrollerMedlemValg {
        return GruppeMedlemRessurs.muligeTilgangsrollerForMedlem(this.gettextCatalog);
    }

    /** Mulige verdier for l.TilgangsrolleMedlem enum med label */
    static muligeTilgangsrollerForMedlem(gettextCatalog: gettextCatalog): TilgangsrollerMedlemValg {
        const valg = <any>{};
        valg[l.TilgangsrolleMedlem.lese] = {
            /// TilgangsrolleMedlem.lese tittel
            tittel: gettextCatalog.getString('Lesetilgang'),

            /// TilgangsrolleMedlem.lese subtittel
            subtittel: gettextCatalog.getString('Bruker kan kun lese informasjon'),

            rolle: l.TilgangsrolleMedlem.lese,
            impliserer: null,
            obligatorisk: true,
            kanFjernesFraEgenBruker: false
        };

        /// TilgangsrolleMedlem.skrive
        valg[l.TilgangsrolleMedlem.skrive] = {
            /// TilgangsrolleMedlem.lese tittel
            tittel: gettextCatalog.getString('Administrator'),

            /// TilgangsrolleMedlem.lese subtittel
            subtittel: gettextCatalog.getString('Bruker kan invitere medlemmer, opprette møter og saker etc.'),

            rolle: l.TilgangsrolleMedlem.skrive,
            impliserer: l.TilgangsrolleMedlem.tilgangIkkePublisert,
            obligatorisk: false,
            kanFjernesFraEgenBruker: false
        };

        /// TilgangsrolleMedlem.tilgangIkkePublisert
        valg[l.TilgangsrolleMedlem.tilgangIkkePublisert] = {
            /// TilgangsrolleMedlem.tilgangIkkePublisert tittel
            tittel: gettextCatalog.getString('Tilgang til ikke-publisert informasjon'),

            /// TilgangsrolleMedlem.tilgangIkkePublisert subtittel
            subtittel: gettextCatalog.getString('Bruker kan se detaljer for ikke-publiserte møter og protokoller'),

            rolle: l.TilgangsrolleMedlem.tilgangIkkePublisert,
            impliserer: null,
            obligatorisk: false,
            kanFjernesFraEgenBruker: true
        };

        /// TilgangsrolleMedlem.tilgangSlettetIHendelseslogg
        valg[l.TilgangsrolleMedlem.tilgangSlettetIHendelseslogg] = {
            /// TilgangsrolleMedlem.tilgangSlettetIHendelseslogg tittel
            tittel: gettextCatalog.getString('Tilgang til hendelseslogg for slettede data'),

            /// TilgangsrolleMedlem.tilgangSlettetIHendelseslogg subtittel
            subtittel: gettextCatalog.getString('Bruker kan se hendelseslogg-innslag for slettede møter, saker, dokumenter osv.'),

            rolle: l.TilgangsrolleMedlem.tilgangSlettetIHendelseslogg,
            impliserer: null,
            obligatorisk: false,
            kanFjernesFraEgenBruker: true
        };

        return valg;
    }
}

export interface TilgangsrollerMedlemValg {
    [verdi: number]: TilgangsrollerMedlemValgInnslag;
}

export interface TilgangsrollerMedlemValgInnslag {
    tittel: string;
    subtittel: string;
    rolle: l.TilgangsrolleMedlem;
    /** tilleggssrolle som automatsik aktiveres om rolle aktiveres*/
    impliserer: l.TilgangsrolleMedlem;
    /** sann dersom denne rollen ikke kan fjernes fra brukere */
    obligatorisk: boolean;
    /** sann dersom bruker ikke selv kan fjerne denne rollen fra seg selv */
    kanFjernesFraEgenBruker: boolean;
}
